/**
 * Fotos de perfil dos profissionais (tiradas no app) para as telas da central.
 *
 * As URLs vêm assinadas do S3 e valem 1h — mantemos um cache em memória de
 * 40 min por `person_id` e agrupamos os pedidos em lote pra que várias telas
 * abertas ao mesmo tempo não repitam a mesma consulta.
 */
import { useEffect, useMemo, useState } from "react";
import api from "./api";

const TTL_MS = 40 * 60 * 1000;
const cache = new Map(); // person_id → { url, ts }
const inflight = new Set(); // ids com requisição em voo (evita pedido duplicado)
let pending = new Set();
let pendingTimer = null;
let pendingPromise = null;
const listeners = new Set();

function fresh(personId) {
  const hit = cache.get(personId);
  if (!hit) return undefined;
  if (Date.now() - hit.ts > TTL_MS) {
    cache.delete(personId);
    return undefined;
  }
  return hit.url;
}

async function flush() {
  const ids = Array.from(pending);
  pending = new Set();
  pendingTimer = null;
  pendingPromise = null;
  if (!ids.length) return;
  ids.forEach((id) => inflight.add(id));
  try {
    const { data } = await api.get("/people/avatars", { params: { ids: ids.join(",") } });
    const photos = data?.photos || {};
    const now = Date.now();
    ids.forEach((id) => cache.set(id, { url: photos[id] || null, ts: now }));
  } catch {
    // Falha silenciosa: as telas seguem com as iniciais
    const now = Date.now();
    ids.forEach((id) => cache.set(id, { url: null, ts: now }));
  }
  ids.forEach((id) => inflight.delete(id));
  listeners.forEach((fn) => fn());
}

function schedule(wanted) {
  const ids = wanted.filter((id) => !inflight.has(id));
  if (!ids.length) return pendingPromise;
  ids.forEach((id) => pending.add(id));
  if (!pendingTimer) {
    pendingPromise = new Promise((resolve) => {
      pendingTimer = setTimeout(() => flush().then(resolve), 60);
    });
  }
  return pendingPromise;
}

/** Mapa `person_id → url` das fotos dos profissionais informados. */
export function useProfPhotos(personIds) {
  const ids = useMemo(
    () => Array.from(new Set((personIds || []).filter(Boolean))).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(personIds || []).filter(Boolean).join("|")],
  );
  const [photos, setPhotos] = useState({});

  useEffect(() => {
    const sync = () => {
      const next = {};
      ids.forEach((id) => {
        const url = fresh(id);
        if (url) next[id] = url;
      });
      setPhotos((prev) => {
        const sameSize = Object.keys(prev).length === Object.keys(next).length;
        if (sameSize && Object.keys(next).every((k) => prev[k] === next[k])) return prev;
        return next;
      });
    };
    sync();
    const missing = ids.filter((id) => fresh(id) === undefined);
    if (missing.length) schedule(missing);
    listeners.add(sync);
    return () => listeners.delete(sync);
  }, [ids]);

  return photos;
}

/** Invalida o cache (ex.: profissional trocou a foto). */
export function clearProfPhotoCache() {
  cache.clear();
  listeners.forEach((fn) => fn());
}
